import * as Sentry from '@sentry/react';
import DRM from 'providers/player/DRM';
import { setLocalStorage, getLocalStorage, removeLocalStorage, guardedLocalStorage } from 'utils/fnStorage';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { FetchResponse } from 'types/ApiTypes';
import { AuthState, TokenResponse } from 'types/AuthTypes';
import { StaticPageRoutes } from 'types/RouteTypes';
import { SECONDS } from 'utils/TimeUnit';
import { getBackoffMs, wait, DEFAULT_ATTEMPTS, isRetryableStatus } from 'utils/fnBackoff';
import { clearQueryCache } from 'providers/QueryProvider';
import { acquireRefreshLock, releaseRefreshLock, waitForRefreshCompletion } from './crossTabRefreshLock';

const NOT_AUTHENTICATED = 401;
const BAD_REQUEST = 400; // mostly for token (Session not active)

let onSignedOutNotice: (() => void) | null = null;
let signedOutNoticeShown = false;

export const setSignedOutNoticeHandler = (handler: (() => void) | null) => {
    onSignedOutNotice = handler;
};

export const resetSignedOutNotice = () => {
    signedOutNoticeShown = false;
};

const sessionEndedResponse = <T>(): FetchResponse<T> => ({ response: null, error: null, status: 0 });

const authUnavailableResponse = <T>(): FetchResponse<T> => ({
    response: null,
    error: new Error('Auth server unreachable'),
    status: 0,
});

const authState: AuthState = {
    token: null,
    refreshToken: null,
    idToken: null,
    authenticated: false,
    clientId: null,
    scope: null,
    refreshExpiresAt: null,
    accessTokenExpiresAt: null,
};

// Error types that are expected and recoverable - shouldn't spam Sentry
const isRecoverableAuthError = (error: any): boolean => {
    if (!error) return false;

    const errorMessage = error.error_description || error.error || error.message || String(error);

    const recoverablePatterns = ['Token is not active', 'Token expired', 'invalid_grant', 'Session not active', 'Failed to refresh token'];

    return recoverablePatterns.some((pattern) => errorMessage.toLowerCase().includes(pattern.toLowerCase()));
};

const captureAuthError = (e: any, context?: { tokenType?: string; attempt?: number }) => {
    console.warn('Auth error:', e, context);

    // Only send to Sentry if it's not a recoverable error
    if (!isRecoverableAuthError(e)) {
        Sentry.captureException(e, {
            tags: {
                auth_error: true,
                ...(context?.tokenType && { token_type: context.tokenType }),
            },
            extra: context,
        });
    }
};

const EXPIRATION_BUFFER_SECONDS = 10;

const isRefreshTokenExpired = (): boolean => {
    if (!authState.refreshExpiresAt) return false;
    return Date.now() >= authState.refreshExpiresAt;
};

const isAccessTokenExpired = (): boolean => {
    if (authState.token && !authState.accessTokenExpiresAt) {
        return true;
    }
    if (!authState.accessTokenExpiresAt) return false;
    return Date.now() >= authState.accessTokenExpiresAt;
};

const getAuthBaseUrl = () =>
    `${ApplicationConfig.api_config.routes.Auth.auth}/realms/${ApplicationConfig.app_config.auth.realm}/protocol/openid-connect`;

const storeTokenExpirations = (tokens: TokenResponse) => {
    if (tokens.expires_in) {
        const accessExpiresInMs = SECONDS.toMillis(tokens.expires_in - EXPIRATION_BUFFER_SECONDS);
        authState.accessTokenExpiresAt = Date.now() + accessExpiresInMs;
        setLocalStorage('accessTokenExpiresAt', authState.accessTokenExpiresAt.toString());
    }

    if (tokens.refresh_expires_in) {
        const refreshExpiresInMs = SECONDS.toMillis(tokens.refresh_expires_in - EXPIRATION_BUFFER_SECONDS);
        authState.refreshExpiresAt = Date.now() + refreshExpiresInMs;
        setLocalStorage('refreshExpiresAt', authState.refreshExpiresAt.toString());
    }
};

const syncTokensFromStorage = (): void => {
    const storedAccessToken = getLocalStorage('accessToken');
    const storedRefreshToken = getLocalStorage('refreshToken');
    const storedIdToken = getLocalStorage('idToken');
    const storedAccessExpiry = getLocalStorage('accessTokenExpiresAt');
    const storedRefreshExpiry = getLocalStorage('refreshExpiresAt');

    if (storedAccessToken) authState.token = storedAccessToken;
    if (storedRefreshToken) authState.refreshToken = storedRefreshToken;
    if (storedIdToken) authState.idToken = storedIdToken;
    if (storedAccessExpiry) authState.accessTokenExpiresAt = parseInt(storedAccessExpiry, 10);
    if (storedRefreshExpiry) authState.refreshExpiresAt = parseInt(storedRefreshExpiry, 10);
    if (storedAccessToken) authState.authenticated = true;
};

// Sync tokens when another tab updates localStorage
window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === 'accessToken' && event.newValue) {
        try {
            const newAccessToken = JSON.parse(event.newValue);
            if (newAccessToken && newAccessToken !== authState.token) {
                syncTokensFromStorage();
            }
        } catch {
            /* ignore parse errors */
        }
    }
    if (event.key === 'refreshToken' && event.newValue) {
        try {
            const newRefreshToken = JSON.parse(event.newValue);
            if (newRefreshToken && newRefreshToken !== authState.refreshToken) {
                authState.refreshToken = newRefreshToken;
            }
        } catch {
            /* ignore parse errors */
        }
    }
});

const createLoginUrl = (deviceUuid: string, options: any = {}) => {
    const authUrl = getAuthBaseUrl();
    const clientId = ApplicationConfig.app_config.auth.client_id;
    const scope = ApplicationConfig.app_config.auth.scope || 'openid';

    const redirectUri = options.redirectUri || window.location.origin + window.location.pathname;

    let url = `${authUrl}/auth?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(
        redirectUri
    )}&response_type=code&scope=${encodeURIComponent(scope)}&device_uuid=${encodeURIComponent(deviceUuid)}`;

    if (options.prompt) {
        url += `&prompt=${encodeURIComponent(options.prompt)}`;
    }

    return url;
};

let userInfoCache: any = null;
let userInfoPromise: Promise<any> | null = null;
type RefreshResult = 'ok' | 'rejected' | 'unreachable';

let refreshTokenPromise: Promise<RefreshResult> | null = null;
let lastRefreshTimestamp: number = 0;
let refreshFailureCount: number = 0;
let refreshBackoffUntil: number = 0;

// Promise helper that resolves when auth initialization is complete
let authInitPromise: Promise<void> | null = null;
let authInitResolve: (() => void) | null = null;

export const clearUserInfoCache = () => {
    userInfoCache = null;
    userInfoPromise = null;
};

const clearRefreshTokenPromise = () => {
    refreshTokenPromise = null;
};

export const waitForAuthInit = async (): Promise<void> => {
    if (authInitPromise) {
        await authInitPromise;
    }
};

const startAuthInit = () => {
    authInitPromise = new Promise<void>((resolve) => {
        authInitResolve = resolve;
    });
};

const completeAuthInit = () => {
    if (authInitResolve) {
        authInitResolve();
        authInitResolve = null;
    }
    authInitPromise = null;
};

const ACCOUNT_SESSION_TIMEOUT = SECONDS.toMillis(5);

let accountSessionSettled = false;
let accountSessionResolve: (() => void) | null = null;
const accountSessionPromise = new Promise<void>((resolve) => {
    accountSessionResolve = resolve;
});

export const isAccountSessionSettled = (): boolean => accountSessionSettled;

export const completeAccountSession = () => {
    if (accountSessionSettled) {
        return;
    }
    accountSessionSettled = true;
    accountSessionResolve?.();
    accountSessionResolve = null;
};

export const waitForAccountSession = async (timeoutMs: number = ACCOUNT_SESSION_TIMEOUT): Promise<boolean> => {
    if (accountSessionSettled) {
        return true;
    }
    await Promise.race([accountSessionPromise, wait(timeoutMs)]);
    return accountSessionSettled;
};

export const getAccountCacheKey = (): string | null => guardedLocalStorage.getItem('gstr-cache-key');

const clearLocalSession = () => {
    clearUserInfoCache();
    clearRefreshTokenPromise();
    clearQueryCache();
    lastRefreshTimestamp = 0;

    removeLocalStorage('refreshToken');
    removeLocalStorage('accessToken');
    removeLocalStorage('idToken');
    removeLocalStorage('clientId');
    removeLocalStorage('scope');
    removeLocalStorage('forceLogin');
    removeLocalStorage('refreshExpiresAt');
    removeLocalStorage('accessTokenExpiresAt');
    removeLocalStorage('guestToken');
    removeLocalStorage('guestRefreshToken');
    removeLocalStorage('gstr-cache-key');

    authState.token = null;
    authState.refreshToken = null;
    authState.idToken = null;
    authState.authenticated = false;
    authState.clientId = null;
    authState.scope = null;
    authState.refreshExpiresAt = null;
    authState.accessTokenExpiresAt = null;
};

export const logout = (callback: (authenticated) => void) => {
    if (signedOutNoticeShown) {
        clearLocalSession();
        if (callback) {
            callback(false);
        }
        return;
    }

    try {
        const authUrl = getAuthBaseUrl();
        const isSettingsPath = window.location.pathname.startsWith(StaticPageRoutes.SETTINGS);
        const redirectUri = isSettingsPath ? `${window.location.origin}/` : window.location.origin + window.location.pathname;
        const { idToken } = authState;
        const clientId = ApplicationConfig.app_config.auth.client_id;
        const isGuest = authState.scope?.includes('guest');

        clearLocalSession();

        if (isGuest) {
            window.location.href = redirectUri;
            return;
        }

        let logoutUrl = `${authUrl}/logout?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;

        if (idToken && idToken !== 'null' && idToken !== '') {
            logoutUrl += `&id_token_hint=${encodeURIComponent(idToken)}`;
        } else if (clientId) {
            logoutUrl += `&client_id=${encodeURIComponent(clientId)}`;
        }

        window.location.href = logoutUrl;
    } catch (e) {
        captureAuthError(e);
        if (callback) {
            callback(false);
        }
    }
};

// A dropped request (e.g. Safari waking a suspended tab) must not end the session —> retry before giving up (with jittering).
const fetchWithNetworkRetry = async (input: string, init: Parameters<typeof fetch>[1]): Promise<Response> => {
    let lastError: unknown;

    for (let attempt = 0; attempt < DEFAULT_ATTEMPTS; attempt += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            return await fetch(input, init);
        } catch (e) {
            lastError = e;

            if (attempt < DEFAULT_ATTEMPTS - 1) {
                // eslint-disable-next-line no-await-in-loop
                await wait(getBackoffMs(attempt));
            }
        }
    }

    throw lastError;
};

const refreshAccessToken = async (): Promise<RefreshResult> => {
    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }

    const now = Date.now();
    if (lastRefreshTimestamp && now - lastRefreshTimestamp < 100) {
        return 'ok';
    }

    if (refreshBackoffUntil && now < refreshBackoffUntil) {
        return 'unreachable';
    }

    const performRefresh = async (): Promise<RefreshResult> => {
        const authUrl = getAuthBaseUrl();
        const deviceUuid = DRM.getDeviceId();
        const clientId = authState.clientId || ApplicationConfig.app_config.auth.client_id;

        if (!authState.refreshToken) {
            clearRefreshTokenPromise();
            return 'rejected';
        }

        if (isRefreshTokenExpired()) {
            clearRefreshTokenPromise();
            logout(null);
            return 'rejected';
        }

        // Cross-tab coordination: only one tab should refresh at a time
        if (!(await acquireRefreshLock())) {
            // Another tab is refreshing — wait for it to finish
            const otherTabRefreshed = await waitForRefreshCompletion(authState.token);
            if (otherTabRefreshed) {
                syncTokensFromStorage();
                clearRefreshTokenPromise();
                return 'ok';
            }
            // Timeout — try to acquire the now-stale lock
            if (!(await acquireRefreshLock())) {
                clearRefreshTokenPromise();
                return 'unreachable';
            }
        }

        try {
            // Re-read refresh token from localStorage — another tab may have rotated it
            const currentRefreshToken = getLocalStorage('refreshToken') || authState.refreshToken;

            const response = await fetchWithNetworkRetry(`${authUrl}/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: `grant_type=refresh_token&client_id=${encodeURIComponent(clientId)}&refresh_token=${encodeURIComponent(
                    currentRefreshToken
                )}&device_uuid=${encodeURIComponent(deviceUuid)}`,
            });

            if (!response.ok) {
                releaseRefreshLock();
                clearRefreshTokenPromise();
                // Before logging out, check if another tab already refreshed successfully
                const storedAccessToken = getLocalStorage('accessToken');
                if (storedAccessToken && storedAccessToken !== authState.token) {
                    syncTokensFromStorage();
                    return 'ok';
                }
                if (response.status === BAD_REQUEST && onSignedOutNotice) {
                    signedOutNoticeShown = true;
                    clearLocalSession();
                    onSignedOutNotice();
                    return 'rejected';
                }
                logout(null);
                return 'rejected';
            }

            const data = await response.json();

            if (data.access_token) {
                authState.token = data.access_token;
                authState.refreshToken = data.refresh_token || authState.refreshToken;
                authState.idToken = data.id_token || authState.idToken;
                authState.authenticated = true;

                storeTokenExpirations(data);

                if (data.scope) {
                    authState.scope = data.scope;
                    setLocalStorage('scope', data.scope);
                }

                setLocalStorage('accessToken', data.access_token);
                if (data.refresh_token) {
                    setLocalStorage('refreshToken', data.refresh_token);
                }
                if (data.id_token) {
                    setLocalStorage('idToken', data.id_token);
                }

                lastRefreshTimestamp = Date.now();
                releaseRefreshLock();
                clearRefreshTokenPromise();
                return 'ok';
            }

            releaseRefreshLock();
            clearRefreshTokenPromise();
            return 'rejected';
        } catch (e) {
            releaseRefreshLock();
            captureAuthError(e, { tokenType: 'refresh' });
            clearRefreshTokenPromise();
            return 'unreachable';
        }
    };

    refreshTokenPromise = performRefresh();

    const result = await refreshTokenPromise;

    if (result === 'unreachable') {
        refreshFailureCount += 1;
        refreshBackoffUntil = Date.now() + getBackoffMs(refreshFailureCount - 1);
    } else {
        refreshFailureCount = 0;
        refreshBackoffUntil = 0;
    }

    return result;
};

const initWithToken = async (token: string, refreshToken: string, idToken?: string, skipRefresh: boolean = false): Promise<boolean> => {
    try {
        authState.token = token;
        authState.refreshToken = refreshToken;
        if (idToken) {
            authState.idToken = idToken;
        }

        if (isRefreshTokenExpired()) {
            logout(null);
            return false;
        }

        if (!skipRefresh && isAccessTokenExpired()) {
            const refreshResult = await refreshAccessToken();

            if (refreshResult !== 'ok') {
                if (refreshResult === 'rejected') {
                    logout(null);
                }
                return false;
            }
        }

        authState.authenticated = true;

        return true;
    } catch (e) {
        captureAuthError(e);
        return false;
    }
};

const fetchTokens = async (code: string): Promise<TokenResponse | null> => {
    const authUrl = getAuthBaseUrl();
    const deviceUuid = DRM.getDeviceId();
    const clientId = ApplicationConfig.app_config.auth.client_id;
    const redirectUri = window.location.origin + window.location.pathname;

    try {
        const response = await fetch(`${authUrl}/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: `grant_type=authorization_code&client_id=${encodeURIComponent(clientId)}&code=${encodeURIComponent(
                code
            )}&redirect_uri=${encodeURIComponent(redirectUri)}&device_uuid=${encodeURIComponent(deviceUuid)}`,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            captureAuthError(errorData, { tokenType: 'authorization_code' });
            return null;
        }

        const data = await response.json();
        return data;
    } catch (e) {
        captureAuthError(e, { tokenType: 'authorization_code' });
        return null;
    }
};

const fetchGuestToken = async (): Promise<TokenResponse> => {
    const URL = `${ApplicationConfig.api_config.routes.Auth.auth}/realms/${ApplicationConfig.app_config.auth.realm}/protocol/openid-connect/token`;
    // Retry a temporary failure before giving up
    // leaves authState.token null and causes 401s on the first API calls.
    const maxAttempts = 2;
    const deviceUuid = DRM.getDeviceId();
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const response = await fetch(URL, {
                method: 'post',
                body: `grant_type=password&client_id=${
                    ApplicationConfig.app_config.auth.client_id
                }&scope=guest&device_uuid=${encodeURIComponent(deviceUuid)}`,
                headers: new Headers({ 'Content-Type': 'application/x-www-form-urlencoded' }),
            });

            if (response.ok) {
                // eslint-disable-next-line no-await-in-loop
                const parsedResponse = await response.json();
                if (parsedResponse?.access_token && parsedResponse?.refresh_token) {
                    return parsedResponse as TokenResponse;
                }
            }
        } catch (e) {
            if (attempt === maxAttempts) {
                captureAuthError(e, { tokenType: 'guest', attempt });
            }
        }

        if (attempt < maxAttempts) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => {
                setTimeout(resolve, 250 * attempt);
            });
        }
    }
    return { access_token: '', refresh_token: '' };
};

const initializeGuestToken = async (skipForceLoginCheck = false): Promise<{ guestToken: string; guestRefreshToken: string }> => {
    let guestToken = getLocalStorage('guestToken');
    let guestRefreshToken = getLocalStorage('guestRefreshToken');
    const forceLogin = getLocalStorage('forceLogin');

    if (!guestToken && !guestRefreshToken && (!forceLogin || skipForceLoginCheck)) {
        const tokens = await fetchGuestToken();

        if (tokens.access_token && tokens.refresh_token) {
            guestToken = tokens.access_token;
            guestRefreshToken = tokens.refresh_token;
            setLocalStorage('guestToken', guestToken);
            setLocalStorage('guestRefreshToken', guestRefreshToken);
            // Also write to the standard keys so the refresh/sync/retry plumbing
            // (which only reads accessToken/refreshToken) works in guest mode.
            setLocalStorage('accessToken', guestToken);
            setLocalStorage('refreshToken', guestRefreshToken);

            authState.scope = 'guest';
            authState.clientId = ApplicationConfig.app_config.auth.client_id;
            setLocalStorage('scope', 'guest');
            setLocalStorage('clientId', authState.clientId);

            storeTokenExpirations(tokens);
        }
    }

    return { guestToken, guestRefreshToken };
};

export const init = (onInitCallback: (authenticated, accessToken?) => void, onError: () => void) => {
    startAuthInit();

    const storedClientId = getLocalStorage('clientId');
    const storedScope = getLocalStorage('scope');
    const storedRefreshExpiresAt = getLocalStorage('refreshExpiresAt');
    const storedAccessTokenExpiresAt = getLocalStorage('accessTokenExpiresAt');

    if (storedClientId) {
        authState.clientId = storedClientId;
    }
    if (storedScope) {
        authState.scope = storedScope;
    }
    if (storedRefreshExpiresAt) {
        authState.refreshExpiresAt = parseInt(storedRefreshExpiresAt, 10);
    }
    if (storedAccessTokenExpiresAt) {
        authState.accessTokenExpiresAt = parseInt(storedAccessTokenExpiresAt, 10);
    }

    // Check if we have an authorization code in the URL (OAuth redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');

    const fetchTokensAfterLoginRedirect = async (): Promise<boolean> => {
        if (!authCode) {
            return false;
        }

        removeLocalStorage('gstr-cache-key');

        try {
            const tokens = await fetchTokens(authCode);

            if (tokens && tokens.access_token && tokens.refresh_token) {
                const loginClientId = ApplicationConfig.app_config.auth.client_id;
                const loginScope = ApplicationConfig.app_config.auth.scope || 'openid';

                setLocalStorage('accessToken', tokens.access_token);
                setLocalStorage('refreshToken', tokens.refresh_token);
                if (tokens.id_token) {
                    setLocalStorage('idToken', tokens.id_token);
                }
                setLocalStorage('clientId', loginClientId);
                setLocalStorage('scope', loginScope);
                setLocalStorage('forceLogin', true);

                authState.clientId = loginClientId;
                authState.scope = loginScope;

                storeTokenExpirations(tokens);

                // Clear any cached data from guest/previous session
                clearQueryCache();

                const authenticated = await initWithToken(tokens.access_token, tokens.refresh_token, tokens.id_token, true);
                return authenticated;
            }
        } catch (e) {
            captureAuthError(e, { tokenType: 'authorization_code_exchange' });
        } finally {
            // Clean up OAuth parameters from URL (code, session_state, etc.)
            window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);
        }

        return false;
    };

    const tryInit = async () => {
        const authenticatedFromRedirect = await fetchTokensAfterLoginRedirect();
        if (authenticatedFromRedirect) {
            return true;
        }

        let { guestToken, guestRefreshToken } = await initializeGuestToken();
        const refreshToken = getLocalStorage('refreshToken');
        const accessToken = getLocalStorage('accessToken');
        const idToken = getLocalStorage('idToken');

        try {
            let tokenToUse = accessToken || guestToken;
            let refreshTokenToUse = refreshToken || guestRefreshToken;
            const idTokenToUse = idToken;

            if (!tokenToUse || !refreshTokenToUse) {
                const retryResult = await initializeGuestToken(true);
                guestToken = retryResult.guestToken;
                guestRefreshToken = retryResult.guestRefreshToken;
                tokenToUse = accessToken || guestToken;
                refreshTokenToUse = refreshToken || guestRefreshToken;
            }

            if (tokenToUse && refreshTokenToUse) {
                const authenticated = await initWithToken(tokenToUse, refreshTokenToUse, idTokenToUse);

                if (authenticated) {
                    return true;
                }
                removeLocalStorage('guestRefreshToken');
                removeLocalStorage('guestToken');

                const fallback = await initializeGuestToken(true);
                if (fallback.guestToken && fallback.guestRefreshToken) {
                    const guestAuthed = await initWithToken(fallback.guestToken, fallback.guestRefreshToken);
                    if (guestAuthed) {
                        return true;
                    }
                }
                return false;
            }
            return false;
        } catch (e) {
            removeLocalStorage('guestRefreshToken');
            removeLocalStorage('guestToken');

            onError();
            captureAuthError(e);
            throw e;
        }
    };

    tryInit()
        .then(async (authenticated) => {
            if (authenticated) {
                onInitCallback(authenticated, authState.token);

                setLocalStorage('refreshToken', authState.refreshToken);
                setLocalStorage('accessToken', authState.token);
                if (authState.clientId) {
                    setLocalStorage('clientId', authState.clientId);
                }
                if (authState.scope) {
                    setLocalStorage('scope', authState.scope);
                }

                const isGuest = authState.scope?.includes('guest');
                setLocalStorage('isGuestUser', isGuest);
                if (!isGuest) {
                    setLocalStorage('forceLogin', true);
                }
            } else {
                const forceLogin = getLocalStorage('forceLogin');
                onInitCallback(false);

                if (forceLogin) {
                    removeLocalStorage('forceLogin');
                }
            }
        })
        .finally(() => {
            // Signal that auth initialization is complete - API calls can now proceed
            completeAuthInit();
        });
};

export const login = (options, callback: (authenticated) => void) => {
    try {
        const deviceUuid = DRM.getDeviceId();
        const loginUrl = createLoginUrl(deviceUuid, options);

        window.location.href = loginUrl;
    } catch (e) {
        captureAuthError(e);
        if (callback) {
            callback(false);
        }
    }
};

export const userInfo = async (callback: (userInfo) => void) => {
    try {
        const authUrl = getAuthBaseUrl();

        if (!authState.token || !authState.authenticated) {
            callback(null);
            return;
        }

        if (userInfoCache) {
            callback(userInfoCache);
            return;
        }

        if (userInfoPromise) {
            const info = await userInfoPromise;
            callback(info);
            return;
        }

        userInfoPromise = (async () => {
            if (isAccessTokenExpired()) {
                if (isRefreshTokenExpired()) {
                    logout(null);
                    return null;
                }

                const refreshResult = await refreshAccessToken();

                if (refreshResult !== 'ok') {
                    if (refreshResult === 'rejected') {
                        logout(null);
                    }
                    return null;
                }
            }

            const response = await fetch(`${authUrl}/userinfo`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authState.token}`,
                },
            });

            if (!response.ok) {
                if (response.status === NOT_AUTHENTICATED) {
                    if (isRefreshTokenExpired()) {
                        logout(null);
                        userInfoPromise = null;
                        return null;
                    }

                    const refreshResult = await refreshAccessToken();

                    if (refreshResult === 'ok') {
                        const retryResponse = await fetch(`${authUrl}/userinfo`, {
                            method: 'GET',
                            headers: {
                                Authorization: `Bearer ${authState.token}`,
                            },
                        });

                        if (retryResponse.ok) {
                            const info = await retryResponse.json();
                            userInfoCache = info;
                            userInfoPromise = null;
                            return info;
                        }
                    }

                    if (refreshResult !== 'unreachable') {
                        logout(null);
                    }
                }

                userInfoPromise = null;
                return null;
            }

            const info = await response.json();
            userInfoCache = info;
            userInfoPromise = null;
            return info;
        })();

        const info = await userInfoPromise;
        callback(info);
    } catch (e) {
        captureAuthError(e);
        userInfoPromise = null;
        callback(null);
    }
};

// On 401, just retry the request once with the current token.
const updateToken = async <T>(exception: any, toExecute: () => Promise<FetchResponse<T>>) => {
    if (exception?.status === NOT_AUTHENTICATED || exception?.response?.status === NOT_AUTHENTICATED) {
        return toExecute();
    }
    return undefined;
};

const isRetryable = (status?: number) => !status || isRetryableStatus(status);

const executeWithUserTokenInternal = async <T>(toExecute: () => Promise<FetchResponse<T>>, maxAttempts: number) => {
    // Wait for auth initialization to complete before making any API calls
    // This prevents race conditions during login/logout redirects
    await waitForAuthInit();

    if (signedOutNoticeShown) {
        return sessionEndedResponse<T>();
    }

    // If init finished but we have no token try one more guest fetch before firing the request —
    // otherwise it would go out without an Authorization header.
    if (!authState.token) {
        syncTokensFromStorage();
        if (!authState.token && !getLocalStorage('forceLogin')) {
            const fallback = await initializeGuestToken(true);
            if (fallback.guestToken && fallback.guestRefreshToken) {
                await initWithToken(fallback.guestToken, fallback.guestRefreshToken);
            }
        }
        if (!authState.token) {
            return Promise.reject<any>(new Error('No auth token available'));
        }
    }

    // Two-level expiration check:
    // 1. If only access token is expired -> refresh!
    // 2. If refresh token is also expired -> logout!
    if (isAccessTokenExpired()) {
        if (isRefreshTokenExpired()) {
            // Both tokens expired - logout
            logout(null);
            return Promise.reject<any>(new Error('Authentication expired'));
        }

        // Only access token expired - try to refresh
        let refreshResult: RefreshResult = 'rejected';

        try {
            refreshResult = await refreshAccessToken();
        } catch (e) {
            logout(null);
            return Promise.reject<any>(Object.assign(new Error('Token refresh error'), { cause: e }));
        }

        if (refreshResult === 'rejected') {
            logout(null);
            return Promise.reject<any>(new Error('Failed to refresh token'));
        }

        if (refreshResult === 'unreachable') {
            return authUnavailableResponse<T>();
        }
    }

    let attempt = 0;

    /* eslint-disable no-await-in-loop, no-continue */
    for (;;) {
        try {
            const result = await toExecute();

            if (result.status === NOT_AUTHENTICATED) {
                const retryResult = await updateToken({ status: NOT_AUTHENTICATED }, toExecute);
                return retryResult ?? result;
            }

            if (isRetryable(result.status) && attempt < maxAttempts - 1) {
                await wait(getBackoffMs(attempt));
                attempt += 1;
                continue;
            }

            return result;
        } catch (exception) {
            const retryResult = await updateToken(exception, toExecute);
            if (retryResult) {
                return retryResult;
            }

            if (isRetryable(exception?.status ?? exception?.response?.status) && attempt < maxAttempts - 1) {
                await wait(getBackoffMs(attempt));
                attempt += 1;
                continue;
            }

            throw exception;
        }
    }
    /* eslint-enable no-await-in-loop, no-continue */
};

export const executeWithUserToken = async <T>(toExecute: () => Promise<FetchResponse<T>>, maxAttempts = DEFAULT_ATTEMPTS) => {
    try {
        return await executeWithUserTokenInternal<T>(toExecute, maxAttempts);
    } catch (exception) {
        if (signedOutNoticeShown) {
            return sessionEndedResponse<T>();
        }
        throw exception;
    }
};

let missingTokenReported = false;

export const tokenRequestHeader = (includeCacheKey = true) => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    if (authState.token) {
        headers.Authorization = `Bearer ${authState.token}`;
    } else if (!missingTokenReported) {
        missingTokenReported = true;
        captureAuthError(new Error('tokenRequestHeader called with null authState.token'));
    }

    if (includeCacheKey) {
        const cacheKey = guardedLocalStorage.getItem('gstr-cache-key');
        if (cacheKey) {
            headers['GSTR-Account-Cache-Key'] = cacheKey;
        }
    }

    return headers;
};

export const fetchCastTokens = async () => {
    const URL = ApplicationConfig.api_config.routes.Auth.token_exchange;
    const timeout = 5000;

    return executeWithUserToken(async () => {
        const controller = new AbortController();
        const id = window.setTimeout(() => controller.abort(), timeout);
        try {
            const response = await fetch(URL, {
                method: 'post',
                headers: tokenRequestHeader(false),
            });

            clearTimeout(id);

            const parsedResponse = await response.json();

            return {
                response: parsedResponse as { access_token: string; expires_in: number; refresh_token: string },
                error: null,
                status: response.status,
            };
        } catch (e) {
            clearTimeout(id);
            return {
                response: { access_token: '', expires_in: 0, refresh_token: '' },
                error: e,
                status: 500,
            };
        }
    }).then((result) => result.response);
};

export const tokens = () => ({
    accessToken: authState.token,
    refreshToken: authState.refreshToken,
});

export const checkUserSubscription = async (): Promise<{ hasSubscription: boolean; error?: boolean }> => {
    const checkSubscriptionStatus = async (): Promise<{ hasSubscription: boolean; error?: boolean }> => {
        if (!authState.authenticated || !authState.token) {
            return { hasSubscription: false, error: false };
        }

        try {
            return new Promise((resolve) => {
                userInfo((info: any) => {
                    if (!info) {
                        resolve({ hasSubscription: false, error: true });
                        return;
                    }

                    const isGuestUser = info.preferred_username === 'guestuser';

                    if (isGuestUser) {
                        resolve({ hasSubscription: true });
                        return;
                    }

                    const hasSubscription = !!(info.subscription && info.subscription !== '');
                    resolve({ hasSubscription });
                });
            });
        } catch {
            return { hasSubscription: false, error: true };
        }
    };

    try {
        const result = await checkSubscriptionStatus();
        if (!result.error) {
            return result;
        }

        await new Promise<void>((resolve) => {
            setTimeout(resolve, 2000);
        });
        return await checkSubscriptionStatus();
    } catch {
        return { hasSubscription: false, error: true };
    }
};

export const isGuestUser = (): boolean => authState.scope?.includes('guest');
